import { Controller } from "stimulus"

type Manager = { id: string; name: string | null; email_address: string }
type PageResponse = { success: boolean; data: Manager[]; has_more: boolean }

const SCROLL_THRESHOLD_PX = 48
const SEARCH_DEBOUNCE_MS = 250

export default class extends Controller {
  static targets = ["select", "label", "options", "search", "status"]
  static values = { url: String, placeholder: String }

  readonly selectTarget!: HTMLSelectElement
  readonly labelTarget!: HTMLElement
  readonly optionsTarget!: HTMLElement
  readonly searchTarget!: HTMLInputElement
  readonly statusTarget!: HTMLElement
  readonly urlValue!: string
  readonly placeholderValue!: string

  // id -> label, for the options currently ticked (they may not be on any
  // loaded page, so their labels have to be remembered rather than looked up)
  private selected = new Map<string, string>()
  private loadedIds: string[] = []
  private labels = new Map<string, string>()

  private page = 0
  private query = ""
  private hasMore = true
  private loading = false
  private seq = 0
  private everOpened = false
  private searchTimer?: number

  private readonly onDocumentClick = (event: MouseEvent): void => {
    if (!this.element.contains(event.target as Node)) this.close()
  }

  connect(): void {
    this.sync()
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect(): void {
    document.removeEventListener("click", this.onDocumentClick)
    if (this.searchTimer) window.clearTimeout(this.searchTimer)
  }

  // Re-read the <select> after someone else rewrote it (e.g. the bulk row).
  sync(): void {
    this.selected = new Map()
    Array.from(this.selectTarget.selectedOptions).forEach(option => {
      this.selected.set(option.value, option.text)
      this.labels.set(option.value, option.text)
    })
    this.renderLabel()
    this.renderOptions()
  }

  toggle(event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    if (this.selectTarget.disabled) return

    if (this.element.classList.contains("open")) this.close()
    else this.open()
  }

  open(): void {
    document.querySelectorAll(".im-picker.open").forEach(el => {
      if (el !== this.element) el.classList.remove("open")
    })
    this.element.classList.add("open")
    this.triggerEl()?.setAttribute("aria-expanded", "true")

    if (!this.everOpened) {
      this.everOpened = true
      this.reload()
    }
    this.searchTarget.focus()
  }

  close(): void {
    if (!this.element.classList.contains("open")) return
    this.element.classList.remove("open")
    this.triggerEl()?.setAttribute("aria-expanded", "false")
  }

  closeOnEscape(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.close()
      this.triggerEl()?.focus()
    }
  }

  search(): void {
    const value = this.searchTarget.value.trim()
    if (this.searchTimer) window.clearTimeout(this.searchTimer)
    this.searchTimer = window.setTimeout(() => {
      if (value === this.query) return
      this.query = value
      this.reload()
    }, SEARCH_DEBOUNCE_MS)
  }

  scroll(): void {
    if (this.loading || !this.hasMore) return
    const { scrollTop, scrollHeight, clientHeight } = this.optionsTarget
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD_PX) this.fetchPage(false)
  }

  // A checkbox inside the option list changed (the action is bound on the list,
  // so options added by a later page need no rebinding).
  pick(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.type !== "checkbox") return

    const label = this.labels.get(input.value) || input.value
    if (input.checked) this.selected.set(input.value, label)
    else this.selected.delete(input.value)

    input.closest(".dd-opt")?.classList.toggle("active", input.checked)
    this.writeBackToSelect()
    this.renderLabel()
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  private triggerEl(): HTMLElement | null {
    return this.element.querySelector<HTMLElement>("[data-manager-picker-trigger]")
  }

  private writeBackToSelect(): void {
    this.selectTarget.innerHTML = ""
    this.selected.forEach((label, id) => {
      const option = document.createElement("option")
      option.value = id
      option.text = label
      option.selected = true
      this.selectTarget.appendChild(option)
    })
  }

  private renderLabel(): void {
    const count = this.selected.size
    this.labelTarget.textContent =
      count === 0 ? this.placeholderValue
        : count === 1 ? Array.from(this.selected.values())[0]
          : `${count} managers`
    this.element.classList.toggle("has-val", count > 0)
  }

  private reload(): void {
    this.page = 0
    this.hasMore = true
    this.loadedIds = []
    this.fetchPage(true)
  }

  private fetchPage(reset: boolean): void {
    if (this.loading) return
    if (!reset && !this.hasMore) return

    this.loading = true
    const requestSeq = ++this.seq
    const nextPage = reset ? 1 : this.page + 1
    this.setStatus("Loading…")

    // the base url already carries ?as_user=..., so merge rather than append
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("page", String(nextPage))
    if (this.query) url.searchParams.set("q", this.query)
    else url.searchParams.delete("q")

    fetch(url.toString(), { headers: { Accept: "application/json" } })
      .then(response => response.json())
      .then((data: PageResponse) => {
        if (requestSeq !== this.seq) return // a newer request superseded this one
        if (!data || !data.success) throw data

        data.data.forEach(manager => {
          this.labels.set(String(manager.id), manager.name || manager.email_address)
          if (!this.loadedIds.includes(String(manager.id))) this.loadedIds.push(String(manager.id))
        })
        this.page = nextPage
        this.hasMore = !!data.has_more && data.data.length > 0
        this.loading = false
        this.renderOptions()
        this.fillViewportIfNeeded()
      })
      .catch(() => {
        if (requestSeq !== this.seq) return
        this.loading = false
        this.hasMore = false
        this.setStatus("Could not load managers.")
      })
  }

  // A short first page can leave the list unscrollable, which would make the
  // scroll handler unreachable and strand the remaining pages.
  private fillViewportIfNeeded(): void {
    if (this.loading || !this.hasMore) return
    const list = this.optionsTarget
    if (list.clientHeight > 0 && list.scrollHeight <= list.clientHeight + 4) this.fetchPage(false)
  }

  private renderOptions(): void {
    // Ticked managers are pinned to the top so a selection made on page 4 stays
    // visible -- except while searching, where the matches are what was asked for.
    const ids = this.query
      ? this.loadedIds.slice()
      : Array.from(this.selected.keys()).concat(this.loadedIds.filter(id => !this.selected.has(id)))

    this.optionsTarget.innerHTML = ""
    ids.forEach(id => this.optionsTarget.appendChild(this.buildOption(id)))

    if (ids.length === 0 && !this.loading) {
      this.setStatus(this.query ? "No managers match that search." : "No eligible managers.")
    } else {
      this.setStatus(this.loading ? "Loading…" : "")
    }
  }

  private buildOption(id: string): HTMLLabelElement {
    const checked = this.selected.has(id)
    const label = document.createElement("label")
    label.className = `dd-opt${checked ? " active" : ""}`

    const input = document.createElement("input")
    input.type = "checkbox"
    input.value = id
    input.checked = checked

    const tick = document.createElement("span")
    tick.className = "tick"

    const text = document.createElement("span")
    text.className = "dd-opt-label"
    text.textContent = this.labels.get(id) || id

    label.append(input, tick, text)
    return label
  }

  private setStatus(message: string): void {
    this.statusTarget.textContent = message
    this.statusTarget.hidden = message === ""
  }
}
